import { lazy } from "react";
import { Helmet } from "react-helmet-async";
import { getKeywordLanding } from "../data/keywordLandings";
import { getLanding } from "../data/seoLanding";
import { Navigate, useLocation } from "react-router-dom";
import { extractCitySlug, cityPath, withLang } from "../i18n/langPath";
import { isKnownCity } from "../data/cities";

// Heavy SEO data (keywordLandings + seoLanding) lives in THIS lazy chunk, so it
// is kept OUT of the main bundle — the homepage never loads it, which improves FCP/LCP.
const LandingPage = lazy(() => import("../pages/LandingPage"));
const CityPage = lazy(() => import("../pages/CityPage"));

// Resolves an ambiguous single URL segment to a keyword landing, city page or the
// provided fallback (usually the home page). Rendered only for non-home segments.
export default function LandingResolver({ seg, lang, fallback = null }) {
  const { search, hash } = useLocation();
  if (getKeywordLanding(seg)) return <LandingPage slug={seg} />;
  const citySlug = extractCitySlug(`/${seg}`, lang);
  if (citySlug) {
    // Alias URL of a known city (e.g. /fr/taxi-marly-to-zurich-airport) -> preferred city URL.
    const preferred = cityPath(citySlug, lang);
    if (isKnownCity(citySlug) && withLang(`/${seg}`, lang) !== preferred) {
      return <Navigate to={preferred + search + hash} replace />;
    }
    return <CityPage citySlug={citySlug} />;
  }
  if (getLanding(seg)) return <LandingPage slug={seg} />;
  // Unknown segment: we still show the homepage for UX, but tell search engines
  // NOT to index this URL (it would otherwise be a soft-404 / duplicate of home).
  return (
    <>
      <Helmet><meta name="robots" content="noindex, follow" /></Helmet>
      {fallback}
    </>
  );
}
